import {
  POLICY_INITIATIVE_FIELD_VIEW_CONFIG,
  PolicyInitiativeStep,
  PolicyInitiativeView
} from '../../../../shared/components/policy-initiative-view/models/policy-initiative-view.model'
import { PolicyInitiativeModel } from '../../models/policy-initiative.model'
import {
  commonStep1Fields,
  commonStep3Fields,
  fieldDefinition,
  IntergovernmentalGovernanceFields,
  IntergovernmentalPolicyFrameworksFields,
  NationalGovernanceFields,
  NationalProgrammesFields,
  NationalRegulationsFields,
  NationalStrategyFields,
  NationalStrategyStep2Fields,
  step1Order
} from '../models/policy-initiative-detail.model'

const step1Fields = new Set([
  ...commonStep1Fields,
  ...IntergovernmentalPolicyFrameworksFields,
  ...IntergovernmentalGovernanceFields,
  ...NationalStrategyFields,
  ...NationalGovernanceFields,
  ...NationalRegulationsFields,
  ...NationalProgrammesFields
])

const step2Fields = new Set([...NationalStrategyStep2Fields])

const step3Fields = new Set([...commonStep3Fields])

export const getPolicyInitiativeView = (
  policyInitiative: PolicyInitiativeModel
): PolicyInitiativeView[] => {
  const definition = fieldDefinition.get(policyInitiative.category)

  if (!definition) return []

  const allowedKeys = new Set(definition)

  const view: PolicyInitiativeView[] = []

  Object.entries(policyInitiative).forEach(([k, v]) => {
    const key = k as keyof PolicyInitiativeModel

    if (!allowedKeys.has(key as string)) {
      return
    }

    const isEmptyArray = Array.isArray(v) && v.length === 0
    if (v === null || v === undefined || isEmptyArray) {
      return
    }

    const commonProps = {
      key,
      label: getLabel(key),
      step: getStep(key)
    }

    const config = POLICY_INITIATIVE_FIELD_VIEW_CONFIG[key]
    // If the field has specific rendering configs we set them. Otherwise we use the default text type.
    if (config) {
      view.push({
        ...commonProps,
        value: config.transform(v),
        type: config.type,
        multiValue: config.multiValue,
        mainProperty: config.mainProperty,
        secondaryProperty: config.secondaryProperty
      })
    } else {
      view.push({
        ...commonProps,
        value: v as string | number | boolean,
        type: 'text'
      })
    }
  })

  return view
}

const getStep = (key: keyof PolicyInitiativeModel): PolicyInitiativeStep => {
  if (step1Fields.has(key)) return 1
  if (step2Fields.has(key)) return 2
  if (step3Fields.has(key)) return 3
  return 1
}

export const getOrder = (key: string, step: number): number => {
  if (step === 1) {
    const index = step1Order.indexOf(key)
    return index !== -1 ? index : 999
  }
  return 0
}

const getLabel = (key: keyof PolicyInitiativeModel): string =>
  LABELS[key] ?? key

const LABELS: Record<string, string> = {
  englishName: 'English Name',
  originalName: 'Name in original language',
  category: 'Category',
  initiativeType: 'Initiative Type',
  principles: 'Principles',
  status: 'Status',
  startYear: 'Start Year',
  endYear: 'End Year',
  targetSectors: 'Target Sectors',
  tags: 'Tags',
  description: 'Description',
  intergovernmentalOrganisation: 'Intergovernmental Organisation',
  extentBinding: 'Extent Binding',
  budgetAvailable: 'Budget Available',
  budget: 'Budget',
  gaiinCountry: 'Country',
  responsibleOrganisation: 'Responsible Organisation',
  engagementMechanisms: 'Engagement Mechanisms',
  hasMonitoringMechanism: 'Monitoring Mechanism',
  monitoringMechanismDescription: 'Monitoring Mechanism Description',
  sourceFiles: 'Source Files',
  relevantUrls: 'Relevant URLs',
  images: 'Images',
  relevantFiles: 'Relevant Files',
  moreInfos: 'More Information',
  isEvaluated: 'Is Evaluated',
  evaluationBy: 'Evaluation By',
  evaluationResultsIsPubliclyAvailable:
    'Evaluation Results Is Publicly Available',
  evaluationDescription: 'Evaluation Description',
  evaluationUrls: 'Evaluation URLs',
  evaluationFiles: 'Evaluation files'
}
